import React, { useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import type { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import { Tag, EchartsWithTheme, useTheme, Empty, Tooltip, EllipsisSpan, Button } from '@maxtropy/components';
import { DoubleRightOutlined } from '@ant-design/icons';
import { keepOneOrLine, keepTwoOrLine, keepPercentOrLine, keepZeroOrLine } from '@/utils/utils';
import {
  apiV2ReportDataWaterLongFlowPost,
  apiV2ReportDataWaterLossRateLeftPost,
  apiV2ReportDataWaterLossRateRightPost,
  apiV2ReportConfigDetailPost,
} from '@maxtropy/device-customer-apis-v2';
import CardBox from '../CardBox';
import { ReportType, getReportTimeRange } from '../../const';
import { getDeviationTrendChartOption } from './charts';
import styles from './index.module.scss';

interface WaterAnalysisProps {
  reportType: ReportType;
  selectedDate: Dayjs;
}

const WaterAnalysis: React.FC<WaterAnalysisProps> = ({ reportType, selectedDate }) => {
  const theme = useTheme();

  // 选中的偏差点位索引
  const [selectedPointIndex, setSelectedPointIndex] = useState<number>(0);

  // 构建请求参数（公共时间范围）
  const requestParams = useMemo(() => getReportTimeRange(reportType, selectedDate), [reportType, selectedDate]);

  // 获取配置详情（偏差阈值）
  const { data: configData } = useRequest(() => apiV2ReportConfigDetailPost({}));

  // 偏差阈值（直接使用，不乘以100）
  const deviationThreshold = useMemo(() => {
    return keepTwoOrLine(configData?.lossRateThreshold);
  }, [configData]);

  // 调用长流水接口
  const { data: longFlowData, loading: longFlowLoading } = useRequest(
    () => apiV2ReportDataWaterLongFlowPost(requestParams),
    {
      refreshDeps: [requestParams],
    }
  );

  // 调用偏差分析左侧列表接口
  const { data: lossRateLeftData, loading: lossRateLeftLoading } = useRequest(
    () => apiV2ReportDataWaterLossRateLeftPost(requestParams),
    {
      refreshDeps: [requestParams],
    }
  );

  // 长流水排名数据（从 API 返回数据计算百分比）
  const longFlowRankings = useMemo(() => {
    if (!longFlowData?.top5Points || longFlowData.top5Points.length === 0) return [];
    const maxUsage = Math.max(...longFlowData.top5Points.map(p => p.usage || 0));
    return longFlowData.top5Points.map(item => ({
      name: item.deviceName || '--',
      usage: item.usage || 0,
      percent: maxUsage > 0 ? Math.round(((item.usage || 0) / maxUsage) * 100) : 0,
    }));
  }, [longFlowData]);

  // 偏差点位列表（从左侧接口获取）
  const deviationPoints = useMemo(() => {
    if (!lossRateLeftData?.nodeList || lossRateLeftData.nodeList.length === 0) {
      return [];
    }
    return lossRateLeftData.nodeList.map(item => ({
      nodeId: item.nodeId,
      name: item.nodeName || '--',
    }));
  }, [lossRateLeftData]);

  // 拓扑ID（用于跳转）
  const topologyId = lossRateLeftData?.topologyId;

  // 当前选中点位的数据
  const selectedPointData = useMemo(() => {
    return deviationPoints[selectedPointIndex];
  }, [deviationPoints, selectedPointIndex]);

  // 偏差分析右侧接口请求参数（依赖选中的 nodeId）
  const lossRateRightParams = useMemo(() => {
    // 如果左侧没有数据或没有选中有效点位，不构建请求参数（避免使用旧的 nodeId）
    if (deviationPoints.length === 0 || !selectedPointData?.nodeId) {
      return null;
    }
    return {
      nodeId: selectedPointData.nodeId,
    };
  }, [selectedPointData, deviationPoints.length]);

  // 调用偏差分析右侧接口
  const { data: lossRateRightData, loading: lossRateRightLoading } = useRequest(
    () =>
      apiV2ReportDataWaterLossRateRightPost({
        ...requestParams, // ← 时间参数在这里传入，但不作为依赖
        ...lossRateRightParams,
      }),
    {
      refreshDeps: [lossRateRightParams], // ← 只依赖 nodeId 参数，不依赖时间参数
      // 右侧请求依赖左侧：
      // 1. 左侧接口加载完成（!lossRateLeftLoading）
      // 2. 左侧有数据（lossRateRightParams 有效）
      ready: !lossRateLeftLoading && !!lossRateRightParams,
    }
  );

  // 偏差率趋势数据（从右侧接口获取）
  const deviationTrendData = useMemo(() => {
    if (!lossRateRightData?.timeLossList || lossRateRightData.timeLossList.length === 0) {
      return [];
    }
    return lossRateRightData.timeLossList.map(item => ({
      time: item.ts ? new Date(item.ts).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }) : '--',
      rate: item.lossPercentage || 0,
    }));
  }, [lossRateRightData]);

  // 初始化选中第一个点位（有数据时选中第一个，无数据时清除选中状态）
  useEffect(() => {
    if (deviationPoints.length > 0) {
      setSelectedPointIndex(0);
    } else {
      setSelectedPointIndex(-1); // 清除选中状态
    }
  }, [deviationPoints]);

  // 图表配置 - 根据选中的点位动态生成
  const deviationTrendOption = useMemo(
    () => getDeviationTrendChartOption(deviationTrendData, selectedPointData?.name),
    [deviationTrendData, selectedPointData]
  );

  const loading = longFlowLoading || lossRateLeftLoading || lossRateRightLoading;

  // 排名颜色规则（与 RankingCard 一致）
  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return '#B34043';
      case 2:
        return '#B3552C';
      case 3:
        return '#B3923D';
      default:
        return '#1B9D6C';
    }
  };

  return (
    <CardBox title="用水分析">
      {/* 第一行：长流水分析(1/3) + 偏差分析(2/3) */}
      <div className={styles.analysis_row}>
        {/* 长流水分析 - 左侧占1/3 */}
        <div className={styles.long_flow_section}>
          <div className={styles.section_title_row}>
            <span className={styles.section_title}>长流水分析</span>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => window.open('/longflow/monitoring/monitor', '_blank')}
            >
              更多
              <DoubleRightOutlined />
            </Button>
          </div>
          {/* 分析时间段 - Tag展示 */}
          <div className={styles.time_tags}>
            {longFlowData?.analysisPeriods?.map((period, i) => (
              <Tag key={i} color="default">
                {period.startTime}~{period.endTime}
              </Tag>
            ))}
          </div>
          {/* 统计卡片盒子 */}
          <div className={styles.stat_box}>
            <div className={styles.stat_item}>
              <div className={styles.stat_label}>长流水点位</div>
              <div className={styles.stat_value_cyan}>{keepZeroOrLine(longFlowData?.pointCount)} 个</div>
            </div>
            <div className={styles.stat_divider} />
            <div className={styles.stat_item}>
              <div className={styles.stat_label}>长流水用量(m³)</div>
              <div className={styles.stat_value_orange}>{keepOneOrLine(longFlowData?.totalUsage)}</div>
            </div>
          </div>
          {/* 长流水排名表头 */}
          <div className={styles.long_flow_header}>
            <span className={styles.header_rank}>排名</span>
            <span className={styles.header_name}>点位名称</span>
            <span className={styles.header_usage}>累计流量(m³)</span>
          </div>
          {/* 长流水排名列表 */}
          <div className={styles.long_flow_list}>
            {longFlowRankings.length === 0 ? (
              <div className={styles.empty_wrapper}>
                <Empty />
              </div>
            ) : (
              longFlowRankings.map((item, i) => (
                <div key={item.name} className={styles.long_flow_item}>
                  <span className={styles.long_flow_rank} style={{ background: getRankColor(i + 1) }}>
                    {i + 1}
                  </span>
                  <Tooltip title={item.name}>
                    <span className={styles.long_flow_name}>{item.name}</span>
                  </Tooltip>
                  <div className={styles.long_flow_right}>
                    <div className={styles.long_flow_progress}>
                      <div
                        className={styles.long_flow_fill}
                        style={{ width: `${item.percent}%`, background: '#FF9247' }}
                      />
                    </div>
                    <span className={styles.long_flow_usage}>{keepTwoOrLine(item.usage)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 偏差分析 - 右侧占2/3 */}
        <div className={styles.deviation_section}>
          <div className={styles.deviation_header}>
            <div className={styles.deviation_title_group}>
              <span className={styles.section_title}>偏差分析</span>
              <span className={styles.deviation_subtitle}>小时偏差超过{deviationThreshold}%点位</span>
            </div>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                const nodeId = selectedPointData?.nodeId;
                const params = new URLSearchParams();
                if (topologyId) params.set('topologyId', String(topologyId));
                if (nodeId) params.set('nodeId', String(nodeId));
                const queryStr = params.toString();
                window.open(`/energyMedia/topology/analysis${queryStr ? `?${queryStr}` : ''}`, '_blank');
              }}
            >
              更多
              <DoubleRightOutlined />
            </Button>
          </div>
          {/* 偏差内容：左侧点位列表 + 右侧统计和图表 */}
          <div className={styles.deviation_content}>
            {/* 左侧：偏差点位列表（可点击选择） */}
            <div className={styles.deviation_left}>
              {deviationPoints.length === 0 ? (
                <div className={styles.empty_wrapper}>
                  <Empty />
                </div>
              ) : (
                <div className={styles.deviation_list_wrapper}>
                  {deviationPoints.map((item, i) => (
                    <div
                      key={item.nodeId || i}
                      className={classNames(
                        styles.deviation_list_item,
                        selectedPointIndex === i ? styles.deviation_list_item_selected : ''
                      )}
                      onClick={() => setSelectedPointIndex(i)}
                    >
                      <span className={styles.deviation_list_rank}>{i + 1}</span>
                      <span className={styles.deviation_list_name}>{item.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* 右侧：统计卡片 + 图表 */}
            <div className={styles.deviation_right}>
              {/* 左侧无数据时显示空状态 */}
              {deviationPoints.length === 0 ? (
                <div className={styles.empty_wrapper}>
                  <Empty />
                </div>
              ) : (
                <>
                  {/* 统计卡片盒子 */}
                  <div className={styles.deviation_stat_box}>
                    <div className={styles.deviation_stat_item}>
                      <div className={styles.deviation_stat_label}>进水(m³)</div>
                      <div className={styles.deviation_stat_value_blue}>
                        {keepTwoOrLine(lossRateRightData?.entryData)}
                      </div>
                    </div>
                    <div className={styles.deviation_stat_divider} />
                    <div className={styles.deviation_stat_item}>
                      <div className={styles.deviation_stat_label}>出水(m³)</div>
                      <div className={styles.deviation_stat_value_green}>
                        {keepTwoOrLine(lossRateRightData?.exitData)}
                      </div>
                    </div>
                    <div className={styles.deviation_stat_divider} />
                    <div className={styles.deviation_stat_item}>
                      <div className={styles.deviation_stat_label}>偏差量(m³)</div>
                      <div className={styles.deviation_stat_value_orange}>
                        {keepTwoOrLine(lossRateRightData?.lossData)}
                      </div>
                    </div>
                    <div className={styles.deviation_stat_divider} />
                    <div className={styles.deviation_stat_item}>
                      <div className={styles.deviation_stat_label}>偏差率</div>
                      <div className={styles.deviation_stat_value_yellow}>
                        {keepPercentOrLine(lossRateRightData?.lossPercentage)}%
                      </div>
                    </div>
                  </div>
                  {/* 偏差率趋势图 */}
                  <div className={styles.deviation_chart_title}>
                    {selectedPointData?.name} - 近三日偏差率趋势 (小时)
                  </div>
                  <div className={styles.chart_container_sm}>
                    <EchartsWithTheme
                      theme={theme}
                      loading={loading}
                      empty={{ isShowEmpty: !lossRateRightData, emptyNode: <Empty /> }}
                      option={deviationTrendOption}
                      style={{ height: '100%', width: '100%' }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </CardBox>
  );
};

export default WaterAnalysis;
